import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const VehicleForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);

    const [vehicleNumber, setVehicleNumber] = useState("");
    const [type, setType] = useState("");
    const [active, setActive] = useState(true);

    useEffect(() => {
        if (!isEditMode) {
            setLoading(false);
            return;
        }
        const fetchVehicle = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/vehicles');
                const vehicle = data.vehicles.find((v) => v._id === id);
                if (!vehicle) {
                    enqueueSnackbar("Vehicle not found", { variant: "error" });
                    navigate('/admin/vehicles');
                    return;
                }
                setVehicleNumber(vehicle.vehicleNumber);
                setType(vehicle.type || "");
                setActive(vehicle.active);
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load vehicle", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchVehicle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const submitHandler = async (e) => {
        e.preventDefault();
        if (!vehicleNumber.trim()) return enqueueSnackbar("Please enter a vehicle number", { variant: "warning" });

        setSaving(true);
        try {
            if (isEditMode) {
                await axios.put(`/api/v1/admin/vehicle/${id}`, { vehicleNumber, type, active });
                enqueueSnackbar("Vehicle updated", { variant: "success" });
            } else {
                await axios.post('/api/v1/admin/vehicle/new', { vehicleNumber, type, active });
                enqueueSnackbar("Vehicle created", { variant: "success" });
            }
            navigate('/admin/vehicles');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save vehicle", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: ${isEditMode ? "Edit" : "New"} Vehicle | 1313 Mart`} />
            {saving && <BackdropLoader />}

            <form onSubmit={submitHandler} className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6" id="vehicleform">

                <h1 className="text-lg font-medium uppercase">{isEditMode ? "Edit" : "New"} Vehicle</h1>

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField label="Vehicle Number" variant="outlined" size="small" required fullWidth value={vehicleNumber} onChange={(e) => setVehicleNumber(e.target.value)} />
                    <TextField label="Type (e.g. Van, Motorbike)" variant="outlined" size="small" fullWidth value={type} onChange={(e) => setType(e.target.value)} />
                </div>

                <FormControlLabel
                    control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />}
                    label="Active (available for assignment)"
                />

                <div className="flex justify-end">
                    <input form="vehicleform" type="submit" className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg cursor-pointer" value={isEditMode ? "Save Changes" : "Create Vehicle"} />
                </div>

            </form>
        </>
    );
};

export default VehicleForm;
